// Pre-processing of model Markdown before rendering with remark-math/KaTeX.
//
// - `\( … \)` → `$…$` and `\[ … \]` → `$$ … $$` (models often emit LaTeX delimiters)
// - a line that only contains `$$ … $$` becomes a display-math block
// - a `$` that cannot start inline math (Pandoc rules: no space after the
//   opening, no space before / digit after the closing `$`) is escaped, so
//   prices like "$5 und $10" stay text
// Fenced code blocks and inline code spans are left untouched.

const PLACEHOLDER = '\u0000';

export function normalizeMarkdown(markdown: string): string {
  const segments = splitFences(markdown.replace(/\r\n?/g, '\n'));
  return segments.map((segment) => (segment.code ? segment.text : normalizeText(segment.text))).join('');
}

interface Segment {
  text: string;
  code: boolean;
}

/** Splits into fenced-code and prose segments (an unclosed fence runs to the end, as in CommonMark). */
function splitFences(markdown: string): Segment[] {
  const lines = markdown.split('\n');
  const segments: Segment[] = [];
  let current: string[] = [];
  let fence: { char: string; length: number } | null = null;

  const flush = (code: boolean) => {
    // Each entry already carries its trailing newline.
    if (current.length) segments.push({ text: current.join(''), code });
    current = [];
  };

  lines.forEach((line, index) => {
    const withNewline = index < lines.length - 1 ? line + '\n' : line;
    const match = /^ {0,3}(`{3,}|~{3,})/.exec(line);
    if (!fence && match?.[1]) {
      flush(false);
      fence = { char: match[1][0]!, length: match[1].length };
      current.push(withNewline);
      return;
    }
    if (fence) {
      current.push(withNewline);
      const close = /^ {0,3}(`{3,}|~{3,})\s*$/.exec(line);
      if (close?.[1] && close[1][0] === fence.char && close[1].length >= fence.length) {
        flush(true);
        fence = null;
      }
      return;
    }
    current.push(withNewline);
  });
  flush(fence !== null);
  return segments;
}

function normalizeText(text: string): string {
  // Protect inline code spans.
  const codeSpans: string[] = [];
  let result = text.replace(/(`+)([\s\S]*?[^`])\1(?!`)/g, (span) => {
    codeSpans.push(span);
    return `${PLACEHOLDER}${codeSpans.length - 1}${PLACEHOLDER}`;
  });

  // \[ … \] → display math on its own lines.
  result = result.replace(/\\\[([\s\S]+?)\\\]/g, (_, inner: string) => `\n$$\n${inner.trim()}\n$$\n`);
  // \( … \) → inline math.
  result = result.replace(/\\\(([\s\S]+?)\\\)/g, (_, inner: string) => `$${inner.trim()}$`);
  // A line that is only `$$ … $$` → display block.
  result = result.replace(/^([ \t]*)\$\$([^\n]+?)\$\$[ \t]*$/gm, (_, indent: string, inner: string) =>
    `${indent}$$\n${indent}${inner.trim()}\n${indent}$$`,
  );

  result = escapeStrayDollars(result);

  return result.replace(new RegExp(`${PLACEHOLDER}(\\d+)${PLACEHOLDER}`, 'g'), (_, i: string) => codeSpans[Number(i)] ?? '');
}

function escapeStrayDollars(text: string): string {
  const lines = text.split('\n');
  let inDisplay = false;
  return lines
    .map((line) => {
      if (inDisplay) {
        if (line.includes('$$')) inDisplay = false;
        return line;
      }
      let out = '';
      let i = 0;
      while (i < line.length) {
        const ch = line[i]!;
        if (ch === '\\') {
          out += line.slice(i, i + 2);
          i += 2;
          continue;
        }
        if (ch === PLACEHOLDER) {
          const end = line.indexOf(PLACEHOLDER, i + 1);
          const stop = end === -1 ? line.length : end + 1;
          out += line.slice(i, stop);
          i = stop;
          continue;
        }
        if (ch !== '$') {
          out += ch;
          i++;
          continue;
        }
        if (line[i + 1] === '$') {
          const close = line.indexOf('$$', i + 2);
          if (close === -1) {
            // Opens a display block that continues on the next lines.
            inDisplay = true;
            out += line.slice(i);
            break;
          }
          out += line.slice(i, close + 2);
          i = close + 2;
          continue;
        }
        const close = findInlineClose(line, i);
        if (close === -1) {
          out += '\\$';
          i++;
          continue;
        }
        out += line.slice(i, close + 1);
        i = close + 1;
      }
      return out;
    })
    .join('\n');
}

/** Index of the `$` closing inline math opened at `open`, or -1. */
function findInlineClose(line: string, open: number): number {
  const next = line[open + 1];
  if (next === undefined || /\s/.test(next)) return -1;
  for (let j = open + 1; j < line.length; j++) {
    const ch = line[j];
    if (ch === '\\') {
      j++;
      continue;
    }
    if (ch === PLACEHOLDER) return -1;
    if (ch !== '$') continue;
    if (line[j + 1] === '$') return -1;
    const before = line[j - 1]!;
    const after = line[j + 1];
    if (/\s/.test(before) || (after !== undefined && /\d/.test(after))) continue;
    return j;
  }
  return -1;
}

// ---------------------------------------------------------------------------
// Summary heading

export interface SummaryHeading {
  /** Text of the leading `# …` line (the video title). */
  title: string;
  /** Text of the `### …` line right below it ("von <Creator>"), if present. */
  subtitle?: string;
  /** Everything after the heading. */
  body: string;
}

/** `# Title` (optional closing hashes per CommonMark: only after a space). */
const H1_RE = /^[ \t]*#[ \t]+([^\n]*?)(?:[ \t]+#+)?[ \t]*(?:\n|$)/;
const H3_RE = /^[ \t]*###[ \t]+([^\n]*?)(?:[ \t]+#+)?[ \t]*(?:\n|$)/;

/**
 * Splits the heading the server puts in front of every summary
 * (`# <Title>\n\n### von <Creator>\n\n…`) from the body. Returns null when the
 * Markdown does not start with a level-1 heading (e.g. older summaries).
 */
export function splitSummaryHeading(markdown: string): SummaryHeading | null {
  const text = markdown.replace(/\r\n?/g, '\n').replace(/^\s*\n/, '');
  const h1 = H1_RE.exec(text);
  if (!h1?.[1]?.trim()) return null;
  let rest = text.slice(h1[0].length).replace(/^(?:[ \t]*\n)+/, '');
  let subtitle: string | undefined;
  const h3 = H3_RE.exec(rest);
  if (h3?.[1]?.trim()) {
    subtitle = unescapeMarkdown(h3[1].trim());
    rest = rest.slice(h3[0].length).replace(/^(?:[ \t]*\n)+/, '');
  }
  return { title: unescapeMarkdown(h1[1].trim()), ...(subtitle ? { subtitle } : {}), body: rest };
}

/** Removes backslash escapes of ASCII punctuation (`\*` → `*`). */
function unescapeMarkdown(text: string): string {
  return text.replace(/\\([!-/:-@[-`{-~])/g, '$1');
}
